import { useState } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkCardList from '@skyscanner-internal/backpack-web/bpk-component-card-list';
import {
  ACCESSORY_DESKTOP_TYPES,
  LAYOUTS,
} from '@skyscanner-internal/backpack-web/bpk-component-card-list/src/common-types';
import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  PackagesCardsCardProps,
  PackagesCardsProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/PackagesCardsProps';

import STYLES from './PackagesCards.module.scss';

const documentIfExists = globalThis.window === undefined ? null : document;
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);
const IMG_ASPECT_RATIO = 392 / 184;

type PackagesCardsCardComponentProps = PackagesCardsCardProps & {
  index: number;
  trackingComponentName: string;
  buildMBDParam: (
    componentName: string,
    params?: Record<string, string>,
  ) => string;
};

const PackagesCardsCard = ({
  altText,
  buildMBDParam,
  cheapestPackagePrice,
  countryName,
  headline,
  imageUrl,
  index,
  packageSummary,
  redirectUrl,
  trackingComponentName,
}: PackagesCardsCardComponentProps) => {
  const itemTrackingName = `${trackingComponentName}_item`;
  const displayPrice = cheapestPackagePrice
    ? [cheapestPackagePrice.priceFromLabel, cheapestPackagePrice.price]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return (
    <BpkCard
      blank
      padded={false}
      href={redirectUrl}
      rel="noopener noreferrer"
      className={STYLES.PackagesCards__card}
      data-testid={`packages-cards-${index}`}
      data-tracking-element-id={`${itemTrackingName}_${index}`}
      data-tracking-common-params={buildMBDParam(itemTrackingName, {
        destination_name: headline,
      })}
    >
      <div className={STYLES.PackagesCards__image}>
        <LazyLoadedImage
          className={STYLES.PackagesCards__imageContent}
          aspectRatio={IMG_ASPECT_RATIO}
          altText={altText}
          src={imageUrl}
        />
        {countryName && (
          <BpkBadge
            type={BADGE_TYPES.normal}
            className={STYLES.PackagesCards__badge}
          >
            {countryName}
          </BpkBadge>
        )}
      </div>
      <div className={STYLES.PackagesCards__body}>
        <div className={STYLES.PackagesCards__priceRow}>
          <div className={STYLES.PackagesCards__description}>
            {headline && (
              <BpkText
                tagName="p"
                textStyle={TEXT_STYLES.heading4}
                color={TEXT_COLORS.textPrimary}
              >
                {headline}
              </BpkText>
            )}
            {packageSummary && (
              <BpkText
                tagName="p"
                textStyle={TEXT_STYLES.caption}
                color={TEXT_COLORS.textSecondary}
              >
                {packageSummary}
              </BpkText>
            )}
          </div>
          {displayPrice && (
            <BpkPrice
              price={displayPrice}
              trailingText={cheapestPackagePrice?.perPersonLabel}
              size={SIZES.small}
              align={ALIGNS.right}
            />
          )}
        </div>
      </div>
    </BpkCard>
  );
};

const PackagesCards = ({
  cards,
  ctaText,
  ctaUrl,
  id,
  subtitle,
  title,
}: PackagesCardsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const [activeChipIndex, setActiveChipIndex] = useState(0);
  const trackingComponentName = id || rawComponentName;

  if (!cards || cards.length === 0 || !title) {
    return null;
  }

  const allTags = Array.from(new Set(cards.flatMap((card) => card.tags ?? [])));
  const hasChips = allTags.length > 0;

  const filteredCards = hasChips
    ? cards.filter((card) => card.tags?.includes(allTags[activeChipIndex]))
    : cards;

  return (
    <div
      className={STYLES.PackagesCards}
      data-testid="packages-cards"
      data-tracking-element-id={trackingComponentName}
      data-tracking-common-params={buildMBDParam(trackingComponentName)}
    >
      <BpkCardList
        key={hasChips ? allTags[activeChipIndex] : 'all-cards'}
        title={title}
        description={subtitle}
        layoutDesktop={LAYOUTS.row}
        layoutMobile={LAYOUTS.rail}
        accessoryDesktop={ACCESSORY_DESKTOP_TYPES.pagination}
        initiallyShownCardsMobile={1}
        buttonContent={ctaText && ctaUrl ? ctaText : undefined}
        buttonHref={ctaText && ctaUrl ? ctaUrl : undefined}
        chipGroup={
          hasChips ? (
            <BpkMobileScrollContainer>
              {allTags.map((tag, index) => (
                <BpkSelectableChip
                  key={tag}
                  accessibilityLabel={tag}
                  selected={index === activeChipIndex}
                  onClick={() => setActiveChipIndex(index)}
                  className={STYLES.PackagesCards__chip}
                >
                  {tag}
                </BpkSelectableChip>
              ))}
            </BpkMobileScrollContainer>
          ) : undefined
        }
        cardList={filteredCards.map((card, index) => (
          <PackagesCardsCard
            key={card.redirectUrl}
            {...card}
            index={index}
            trackingComponentName={trackingComponentName}
            buildMBDParam={buildMBDParam}
          />
        ))}
      />
    </div>
  );
};

export default PackagesCards;
